import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  ArrowLeft, Loader2, Edit2, Save, X, CheckCircle2, Circle,
  Clock, User, FileText, History, UserPlus, CalendarDays, RefreshCw, Download,
  ClipboardCheck, Mail, Eye, ExternalLink, Send, Printer, Construction, Link2,
  CheckCircle, XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { FASES_ORDEN, getFaseColor, FASES_COLORES, FASES_RUTAS, getUnidadCodigo, getUnidadTipo, formatUnidadAsignada } from './constants';
import ChecklistPreEntrega from './ChecklistPreEntrega';
import { useAuth } from '../../context/AuthContext';
import { DocumentosCRMCard, documentosEfectivos } from './DocumentosCRMCard';
import { EtapaSelect, useEtapasProyecto } from './EtapaSelect';

const TIPOS_UNIDAD = ['Departamento', 'Estacionamiento', 'Depósito', 'Local Comercial', 'Área Común'];
const TIPOS_DOCUMENTO = ['DNI', 'CE', 'Pasaporte', 'RUC'];

const EntregaDetalle = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user: currentUser } = useAuth();
  const [entrega, setEntrega] = useState(null);
  const [loading, setLoading] = useState(true);
  const [historial, setHistorial] = useState([]);
  const [estados, setEstados] = useState([]);
  const [editMode, setEditMode] = useState(null); // null | 'ubicacion' | 'cliente' | 'extra'
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [refreshingCRM, setRefreshingCRM] = useState(false);
  const [datosCRMRefresh, setDatosCRMRefresh] = useState(null);
  const etapasEdicion = useEtapasProyecto(editMode === 'ubicacion' ? entrega?.proyecto_id : null);
  const [pdfPreview, setPdfPreview] = useState(null); // { url, nombre }
  const [showDocsList, setShowDocsList] = useState(false);

  // Asignar arquitecto dialog
  const [showAsignarArq, setShowAsignarArq] = useState(false);

  // Reprogramar cita confirmada dialog
  const [showReprogramar, setShowReprogramar] = useState(false);
  const [reprogForm, setReprogForm] = useState({ fecha: '', hora_inicio: '', hora_fin: '', motivo: '' });
  const [savingReprog, setSavingReprog] = useState(false);
  const [progConfirmada, setProgConfirmada] = useState(null);
  const [showWipModal, setShowWipModal] = useState(false);
  const [arquitectosDisponibles, setArquitectosDisponibles] = useState([]);
  const [selArquitectoId, setSelArquitectoId] = useState('');
  const [loadingArqs, setLoadingArqs] = useState(false);
  const [asignando, setAsignando] = useState(false);
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailPreview, setEmailPreview] = useState(null);
  const [auditConfirmaciones, setAuditConfirmaciones] = useState([]);
  const [auditModal, setAuditModal] = useState(null); // {id, ...} for modal

  // Polling for async PDF extraction
  const [pollingCalculo, setPollingCalculo] = useState(false);

  useEffect(() => {
    if (!entrega?.calculando_fechas) return;
    setPollingCalculo(true);
    const interval = setInterval(async () => {
      try {
        const resp = await api.get(`/entregas/${id}/estado-calculo`);
        if (!resp.data.calculando_fechas) {
          clearInterval(interval);
          setPollingCalculo(false);
          if (resp.data.calculando_fechas_error) {
            toast.error(`Error al extraer fechas: ${resp.data.calculando_fechas_error}`);
          } else {
            toast.success('Fechas contractuales extraídas correctamente de los PDFs');
          }
          loadEntrega(); // Reload to get updated dates
        }
      } catch {
        // Silently retry
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [entrega?.calculando_fechas, id]);

  // Carga inicial consolidada (1 sola llamada API)
  const loadDetalleCompleto = useCallback(async () => {
    try {
      const resp = await api.get(`/entregas/${id}/detalle-completo`);
      const d = resp.data;
      setEntrega(d.entrega);
      setHistorial(d.historial || []);
      setEstados(d.estados || []);
      setEmailLogs(d.email_audit_logs || []);
      setAuditConfirmaciones(d.audit_confirmaciones || []);
      // Programación activa
      if (d.entrega?.programacion_activa_id && d.programaciones?.length) {
        const activa = d.programaciones.find(p => p.id === d.entrega.programacion_activa_id);
        if (activa) setProgConfirmada(activa);
      }
    } catch (err) {
      toast.error('Error al cargar la entrega');
      navigate('/entregas');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  // Recargas parciales post-acción
  const loadEntrega = useCallback(async () => {
    try {
      const resp = await api.get(`/entregas/${id}`);
      setEntrega(resp.data);
      if (resp.data?.programacion_activa_id) {
        try {
          const progsResp = await api.get(`/entregas/${id}/programaciones`);
          const activa = (progsResp.data || []).find(p => p.id === resp.data.programacion_activa_id);
          if (activa) setProgConfirmada(activa);
        } catch { /* ignore */ }
      }
    } catch (err) {
      toast.error('Error al cargar la entrega');
      navigate('/entregas');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const loadHistorial = useCallback(async () => {
    try {
      const resp = await api.get(`/entregas/${id}/historial`);
      setHistorial(resp.data || []);
    } catch (err) {}
  }, [id]);

  const loadEmailLogs = useCallback(async () => {
    try {
      const resp = await api.get(`/entregas/${id}/email-audit-logs`);
      setEmailLogs(resp.data || []);
    } catch (err) {}
  }, [id]);

  useEffect(() => {
    loadDetalleCompleto();
  }, [loadDetalleCompleto]);

  const getEstadoLabel = (estado) => {
    const found = estados.find(e => e.estado === estado);
    return found?.estado_label || estado;
  };

  const getEstadoColor = (estado) => {
    const found = estados.find(e => e.estado === estado);
    return found?.color || '#6B7280';
  };

  const getPasoSiguiente = () => {
    if (!entrega) return null;
    const found = estados.find(e => e.estado === entrega.estado);
    return found?.paso_siguiente || null;
  };

  // Estados que se manejan con navegación a la página de inspección (no con transición genérica)
  const ESTADOS_INSPECCION = ['inspeccion_pendiente', 'inspeccion_en_proceso', 'inspeccion_con_observaciones'];
  const ESTADOS_PROGRAMACION = ['programacion_pendiente', 'esperando_confirmacion', 'programacion_vencida', 'reprogramacion_solicitada', 'fecha_confirmada', 'citacion_notarial'];

  const getTransicionesPosibles = () => {
    if (!entrega) return [];
    // Los estados de inspección y programación se manejan aparte
    if (ESTADOS_INSPECCION.includes(entrega.estado)) return [];
    if (ESTADOS_PROGRAMACION.includes(entrega.estado)) return [];
    const TRANSICIONES = {
      'inspeccion_terminada': ['programacion_pendiente'],
      'entrega_en_proceso': ['entrega_aceptada_sin_obs', 'entrega_aceptada_con_obs_cliente', 'entrega_rechazada_con_obs_cliente', 'entrega_rechazada_con_obs_empresa'],
    };
    return TRANSICIONES[entrega.estado] || [];
  };

  const handleTransicion = async (nuevoEstado) => {
    // Asignar arquitecto — diálogo especial
    if (entrega?.estado === 'sin_arquitecto_asignado') {
      openAsignarArquitecto();
      return;
    }
    // Transición genérica (fases posteriores a inspección)
    setTransitioning(true);
    try {
      await api.post(`/entregas/${id}/transicion`, { nuevo_estado: nuevoEstado });
      toast.success('Transición ejecutada');
      loadEntrega();
      loadHistorial();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error en la transición';
      toast.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setTransitioning(false);
    }
  };

  // --- Acciones específicas de Inspección ---
  const handleIniciarInspeccion = async () => {
    setTransitioning(true);
    try {
      await api.post(`/entregas/${id}/inspecciones`);
      toast.success('Inspección iniciada');
      navigate(`/entregas/${id}/inspeccion`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear inspección');
    } finally {
      setTransitioning(false);
    }
  };

  const handleIrAInspeccion = () => {
    navigate(`/entregas/${id}/inspeccion`);
  };

  const openAsignarArquitecto = async () => {
    setShowAsignarArq(true);
    setSelArquitectoId('');
    setLoadingArqs(true);
    try {
      const resp = await api.get('/entregas/config/arquitectos-disponibles');
      setArquitectosDisponibles(resp.data || []);
    } catch {
      setArquitectosDisponibles([]);
    } finally {
      setLoadingArqs(false);
    }
  };

  const handleAsignarArquitecto = async () => {
    if (!selArquitectoId) {
      toast.error('Selecciona un arquitecto');
      return;
    }
    const arq = arquitectosDisponibles.find(a => a.id === selArquitectoId);
    setAsignando(true);
    try {
      await api.post(`/entregas/${id}/asignar-arquitecto`, {
        arquitecto_id: selArquitectoId,
        arquitecto_nombre: arq?.nombre || null
      });
      toast.success(`Arquitecto ${arq?.nombre || ''} asignado`);
      setShowAsignarArq(false);
      loadEntrega();
      loadHistorial();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error al asignar arquitecto';
      toast.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setAsignando(false);
    }
  };

  const openReprogramarCita = () => {
    const fc = entrega.fecha_cita_confirmada;
    const d = fc ? new Date(fc) : null;
    setReprogForm({
      fecha: d ? d.toISOString().slice(0, 10) : '',
      hora_inicio: d ? d.toISOString().slice(11, 16) : '',
      hora_fin: d ? (() => { const e = new Date(d.getTime() + 60*60*1000); return e.toISOString().slice(11,16); })() : '',
      motivo: ''
    });
    setShowReprogramar(true);
  };

  const handleReprogramarCita = async () => {
    if (!reprogForm.fecha || !reprogForm.hora_inicio || !reprogForm.hora_fin) {
      toast.error('Todos los campos de fecha y hora son obligatorios');
      return;
    }
    if (!reprogForm.motivo.trim()) {
      toast.error('El motivo de reprogramación es obligatorio');
      return;
    }
    setSavingReprog(true);
    try {
      const resp = await api.put(`/entregas/programaciones/${id}/reprogramar-cita-confirmada`, reprogForm);
      toast.success('Cita reprogramada exitosamente');
      setShowReprogramar(false);
      setEntrega(prev => ({ ...prev, fecha_cita_confirmada: resp.data.fecha_cita_confirmada }));
      loadHistorial();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error al reprogramar';
      toast.error(detail);
    } finally {
      setSavingReprog(false);
    }
  };


  const startEdit = (section) => {
    if (!entrega) return;
    setEditMode(section);
    setEditForm({ ...entrega });
  };

  const cancelEdit = () => {
    setEditMode(null);
    setEditForm({});
    setDatosCRMRefresh(null);
  };

  const handleRefreshCRM = async () => {
    const proforma = entrega?.proforma_codigo || entrega?.sperant_proforma_id;
    if (!proforma) {
      toast.error('La entrega no tiene código de proforma');
      return;
    }
    setRefreshingCRM(true);
    try {
      const resp = await api.post('/entregas/crm/consultar', { proforma_codigo: proforma });
      const { datos_formulario, datos_crm, pdfs_disponibles, raw_payload } = resp.data;

      // Enter edit mode with CRM data pre-filled
      setEditMode('ubicacion');
      setEditForm({
        ...entrega,
        proyecto_nombre: datos_formulario.proyecto_nombre || entrega.proyecto_nombre,
        subdivision_id: datos_formulario.subdivision_id || entrega.subdivision_id,
        subdivision_nombre: datos_formulario.subdivision_nombre || entrega.subdivision_nombre,
        unidad_principal: datos_formulario.unidad_principal || entrega.unidad_principal,
        unidades_asignadas: datos_formulario.unidades_asignadas || entrega.unidades_asignadas,
        cliente_nombre: datos_formulario.cliente_nombre || entrega.cliente_nombre,
        cliente_tipo_documento: datos_formulario.cliente_tipo_documento || entrega.cliente_tipo_documento,
        cliente_numero_documento: datos_formulario.cliente_numero_documento || entrega.cliente_numero_documento,
        cliente_email: datos_formulario.cliente_email || entrega.cliente_email,
        cliente_celular: datos_formulario.cliente_celular || entrega.cliente_celular,
        cliente_telefono_fijo: datos_formulario.cliente_telefono_fijo || entrega.cliente_telefono_fijo,
        co_propietarios: datos_formulario.co_propietarios || entrega.co_propietarios,
        premio: datos_formulario.premio || entrega.premio,
      });

      // Store CRM metadata for save
      setDatosCRMRefresh({
        ...datos_crm,
        crm_raw_payload: raw_payload,
      });

      const numPdfs = (pdfs_disponibles.contrato ? 1 : 0) + (pdfs_disponibles.adendas?.length || 0);
      const numOtros = pdfs_disponibles.otros_documentos?.length || 0;
      const pdfMsg = pdfs_disponibles.sin_ccv
        ? (numOtros > 0 ? ` El CCV no vino identificado; se analizarán ${numOtros} documento(s) para identificarlo tras guardar.` : '')
        : (numPdfs > 0 ? ` ${numPdfs} PDF(s) serán re-analizados tras guardar.` : '');
      toast.success(`Datos actualizados desde CRM.${pdfMsg} Presione Guardar para confirmar.`);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error al consultar el CRM';
      toast.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setRefreshingCRM(false);
    }
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      let changes = {};

      if (datosCRMRefresh) {
        // CRM refresh: send ALL updated fields + CRM metadata
        const allFields = [
          'proyecto_nombre', 'subdivision_id', 'subdivision_nombre', 'proforma_codigo',
          'unidad_principal', 'unidades_asignadas',
          'fecha_entrega_contractual', 'fecha_entrega_reprogramada',
          'cliente_nombre', 'cliente_tipo_documento', 'cliente_numero_documento',
          'cliente_email', 'cliente_celular', 'cliente_telefono_fijo',
          'co_propietarios', 'premio', 'notas'
        ];
        for (const f of allFields) {
          if (editForm[f] !== undefined && editForm[f] !== null) {
            changes[f] = editForm[f];
          }
        }
        // Merge CRM metadata
        Object.assign(changes, datosCRMRefresh);
      } else {
        // Normal section edit
        const fields = editMode === 'ubicacion'
          ? ['proyecto_nombre', 'subdivision_id', 'proforma_codigo', 'unidad_principal', 'fecha_entrega_contractual', 'fecha_entrega_reprogramada']
          : editMode === 'cliente'
          ? ['cliente_nombre', 'cliente_tipo_documento', 'cliente_numero_documento', 'cliente_email', 'cliente_celular', 'cliente_telefono_fijo']
          : editMode === 'copropietarios'
          ? ['co_propietarios']
          : ['premio', 'notas'];

        for (const f of fields) {
          if (f === 'co_propietarios') {
            // Comparar arrays por contenido
            if (JSON.stringify(editForm[f] || []) !== JSON.stringify(entrega[f] || [])) {
              changes[f] = editForm[f] || [];
            }
          } else if (JSON.stringify(editForm[f] || null) !== JSON.stringify(entrega[f] || null)) {
            changes[f] = editForm[f] || null;
          }
        }
      }

      if (Object.keys(changes).length === 0) {
        cancelEdit();
        return;
      }

      await api.put(`/entregas/${id}`, changes);
      const isCRMRefresh = !!datosCRMRefresh;
      toast.success(isCRMRefresh ? 'Datos CRM guardados. Recalculando fechas...' : 'Cambios guardados');
      setEditMode(null);
      setDatosCRMRefresh(null);
      loadEntrega();
      loadHistorial();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-[#9d8272]" />
        <span className="ml-2 text-[#888]">Cargando entrega...</span>
      </div>
    );
  }

  if (!entrega) return null;
  const docsEfectivos = documentosEfectivos(entrega);
  const esOrigenCrm = ['crm', 'webhook_crm'].includes(entrega.origen);

  const faseActualIdx = FASES_ORDEN.indexOf(entrega.fase);
  const transicionesPosibles = getTransicionesPosibles();

  return (
    <div className="space-y-4" data-testid="entrega-detalle-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/entregas')} data-testid="btn-volver-lista">
            <ArrowLeft size={16} className="mr-1" /> Volver
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-lg font-bold text-[#4E7A79]" data-testid="entrega-codigo">
                {entrega.codigo}
              </span>
              <Badge
                className="text-xs"
                style={{
                  backgroundColor: `${getFaseColor(entrega.fase)}20`,
                  color: getFaseColor(entrega.fase),
                  borderColor: getFaseColor(entrega.fase)
                }}
                variant="outline"
              >
                {entrega.fase}
              </Badge>
              <Badge
                className="text-xs"
                style={{
                  backgroundColor: `${getEstadoColor(entrega.estado)}20`,
                  color: getEstadoColor(entrega.estado)
                }}
              >
                {getEstadoLabel(entrega.estado)}
              </Badge>
              {entrega.origen !== 'manual' && (
                entrega.crm_consultado_at ? (
                  <Badge variant="outline" className="text-xs border-green-400 text-green-700 bg-green-50" data-testid="badge-crm-sync">
                    CRM sincronizado
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs border-amber-400 text-amber-600 bg-amber-50" data-testid="badge-crm-pendiente">
                    CRM pendiente
                  </Badge>
                )
              )}
            </div>
            <p className="text-sm text-[#888]">{entrega.proyecto_nombre} — {getUnidadCodigo(entrega)}</p>
          </div>
        </div>

        {/* Botón Paso Siguiente — Transiciones genéricas */}
        {transicionesPosibles.length > 0 && (
          <div className="flex gap-2">
            {transicionesPosibles.length === 1 ? (
              <Button
                onClick={() => handleTransicion(transicionesPosibles[0])}
                disabled={transitioning}
                className="bg-[#4E7A79] hover:bg-[#3d6362] text-white"
                data-testid="btn-transicion"
              >
                {transitioning ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
                {getPasoSiguiente() || getEstadoLabel(transicionesPosibles[0])}
              </Button>
            ) : (
              transicionesPosibles.map(t => (
                <Button
                  key={t}
                  onClick={() => handleTransicion(t)}
                  disabled={transitioning}
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  data-testid={`btn-transicion-${t}`}
                >
                  {getEstadoLabel(t)}
                </Button>
              ))
            )}
          </div>
        )}

        {/* Botón Asignar Responsable */}
        {entrega?.estado === 'sin_arquitecto_asignado' && (
          <Button
            onClick={openAsignarArquitecto}
            disabled={transitioning}
            className="bg-[#4E7A79] hover:bg-[#3d6362] text-white"
            data-testid="btn-asignar-responsable"
          >
            <UserPlus size={14} className="mr-1" />
            {getPasoSiguiente() || 'Asignar responsable'}
          </Button>
        )}
        {entrega?.estado === 'liberacion_vencida' && (
          <Button
            onClick={openAsignarArquitecto}
            disabled={transitioning}
            className="bg-[#4E7A79] hover:bg-[#3d6362] text-white"
            data-testid="btn-asignar-responsable-vencida"
          >
            <UserPlus size={14} className="mr-1" />
            {getPasoSiguiente() || 'Asignar responsable'}
          </Button>
        )}

        {/* Botones Inspección — flujo especial */}
        {(entrega?.estado === 'inspeccion_pendiente' || entrega?.estado === 'inspeccion_vencida') && (
          <Button
            onClick={handleIniciarInspeccion}
            disabled={transitioning}
            className="bg-[#4E7A79] hover:bg-[#3d6362] text-white"
            data-testid="btn-iniciar-inspeccion"
          >
            {transitioning ? <Loader2 size={14} className="animate-spin mr-1" /> : <FileText size={14} className="mr-1" />}
            {getPasoSiguiente() || 'Iniciar Inspección'}
          </Button>
        )}
        {entrega?.estado === 'inspeccion_en_proceso' && (
          <Button
            onClick={handleIrAInspeccion}
            className="bg-[#4E7A79] hover:bg-[#3d6362] text-white"
            data-testid="btn-continuar-inspeccion"
          >
            <FileText size={14} className="mr-1" />
            {getPasoSiguiente() || 'Continuar Inspección'}
          </Button>
        )}
        {(entrega?.estado === 'inspeccion_con_observaciones' || entrega?.estado === 'inspeccion_con_observaciones_vencida') && (
          <Button
            onClick={handleIrAInspeccion}
            className="bg-[#9d8272] hover:bg-[#8a7162] text-white"
            data-testid="btn-subsanar-inspeccion"
          >
            <FileText size={14} className="mr-1" />
            {getPasoSiguiente() || 'Subsanar Inspección'}
          </Button>
        )}
        {/* Programación buttons */}
        {entrega?.estado === 'programacion_pendiente' && (
          <Button
            onClick={() => navigate(`/entregas/${id}/programacion`)}
            className="bg-[#5B7FA6] hover:bg-[#4a6d92] text-white"
            data-testid="btn-proponer-fechas"
          >
            <CalendarDays size={14} className="mr-1" />
            {getPasoSiguiente() || 'Proponer Fechas'}
          </Button>
        )}
        {entrega?.estado === 'esperando_confirmacion' && (
          <Button
            onClick={() => navigate(`/entregas/${id}/programacion`)}
            className="bg-[#5B7FA6] hover:bg-[#4a6d92] text-white"
            data-testid="btn-ver-programacion"
          >
            <Clock size={14} className="mr-1" />
            Ver Programación
          </Button>
        )}
        {(entrega?.estado === 'programacion_vencida' || entrega?.estado === 'reprogramacion_solicitada') && (
          <Button
            onClick={() => navigate(`/entregas/${id}/programacion`)}
            className="bg-[#d97706] hover:bg-[#b86606] text-white"
            data-testid="btn-reprogramar"
          >
            <RefreshCw size={14} className="mr-1" />
            {getPasoSiguiente() || 'Reprogramar Fechas'}
          </Button>
        )}
        {entrega?.estado === 'fecha_confirmada' && (
          <Button
            onClick={() => setShowWipModal(true)}
            className="bg-green-600 hover:bg-green-700 text-white"
            data-testid="btn-iniciar-entrega"
          >
            <CheckCircle2 size={14} className="mr-1" />
            Iniciar Entrega
          </Button>
        )}
        {entrega?.estado === 'citacion_notarial' && (
          <Button
            onClick={() => setShowWipModal(true)}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="btn-iniciar-entrega-notarial"
          >
            <FileText size={14} className="mr-1" />
            Iniciar Entrega
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Timeline lateral */}
        <Card className="border border-[#E8E8E7] lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#5A5A5A]">Pipeline</CardTitle>
          </CardHeader>
          <CardContent className="pb-4">
            <div className="space-y-0">
              {FASES_ORDEN.map((fase, idx) => {
                const isCurrent = idx === faseActualIdx;
                const isPast = idx < faseActualIdx;
                const color = FASES_COLORES[fase] || '#6B7280';

                return (
                  <div key={fase} className="flex items-start gap-2.5 relative">
                    {/* Vertical line */}
                    {idx < FASES_ORDEN.length - 1 && (
                      <div
                        className="absolute left-[9px] top-[20px] w-0.5 h-6"
                        style={{ backgroundColor: isPast ? color : '#E8E8E7' }}
                      />
                    )}
                    {/* Circle */}
                    <div className="mt-0.5 shrink-0">
                      {isPast ? (
                        <CheckCircle2 size={18} style={{ color }} />
                      ) : isCurrent ? (
                        <div
                          className="w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center"
                          style={{ borderColor: color, backgroundColor: `${color}30` }}
                        >
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                        </div>
                      ) : (
                        <Circle size={18} className="text-[#D5D5D5]" />
                      )}
                    </div>
                    {/* Label */}
                    {isPast && FASES_RUTAS[fase] ? (
                      <span
                        className="text-sm py-0.5 font-medium cursor-pointer hover:underline"
                        style={{ color }}
                        onClick={() => navigate(FASES_RUTAS[fase](id))}
                        data-testid={`pipeline-link-${fase.toLowerCase()}`}
                      >
                        {fase}
                      </span>
                    ) : (
                      <span
                        className={`text-sm py-0.5 ${isCurrent ? 'font-semibold' : isPast ? 'font-medium' : 'text-[#aaa]'}`}
                        style={{ color: isCurrent || isPast ? color : undefined }}
                      >
                        {fase}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Panel principal */}
        <div className="lg:col-span-3">
          <Tabs defaultValue="info" className="w-full">
            <TabsList className={`grid w-full ${(entrega.origen === 'excel' && entrega.datos_sperant) || esOrigenCrm ? 'grid-cols-4' : 'grid-cols-3'}`}>
              <TabsTrigger value="info" className="text-xs sm:text-sm" data-testid="tab-info">
                <FileText size={14} className="mr-1 hidden sm:inline" /> Información
              </TabsTrigger>
              <TabsTrigger value="checklist" className="text-xs sm:text-sm" data-testid="tab-checklist">
                <ClipboardCheck size={14} className="mr-1 hidden sm:inline" /> Checklist
              </TabsTrigger>
              <TabsTrigger value="historial" className="text-xs sm:text-sm" data-testid="tab-historial">
                <History size={14} className="mr-1 hidden sm:inline" /> Historial
              </TabsTrigger>
              {entrega.datos_sperant && (
                <TabsTrigger value="import" className="text-xs sm:text-sm" data-testid="tab-import">
                  Datos Import
                </TabsTrigger>
              )}
              {esOrigenCrm && (
                <TabsTrigger value="crm" className="text-xs sm:text-sm" data-testid="tab-crm">
                  <ExternalLink size={14} className="mr-1 hidden sm:inline" /> CRM
                </TabsTrigger>
              )}
            </TabsList>

            {/* Tab: Información General */}
            <TabsContent value="info" className="space-y-4 mt-4">
              {/* CRM Refresh Banner */}
              {datosCRMRefresh && editMode === 'ubicacion' && (
                <div className="bg-[#F0F7F6] border border-[#4E7A79]/30 rounded-lg px-4 py-2.5 flex items-center gap-2" data-testid="crm-refresh-banner">
                  <RefreshCw size={16} className="text-[#4E7A79] shrink-0" />
                  <span className="text-sm text-[#4E7A79] font-medium">Datos actualizados desde CRM. Presione Guardar para confirmar.</span>
                </div>
              )}

              {/* Ubicación */}
              <Card className="border border-[#E8E8E7]">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold text-[#4E7A79]">Ubicación</CardTitle>
                    {editMode !== 'ubicacion' ? (
                      <Button variant="ghost" size="sm" onClick={() => startEdit('ubicacion')} className="h-7 text-xs text-[#9d8272]" data-testid="btn-edit-ubicacion">
                        <Edit2 size={12} className="mr-1" /> Editar
                      </Button>
                    ) : (
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={cancelEdit} className="h-7 text-xs"><X size={12} /></Button>
                        <Button size="sm" onClick={saveEdit} disabled={saving} className="h-7 text-xs bg-[#4E7A79] text-white" data-testid="btn-save-ubicacion">
                          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} className="mr-1" />} Guardar
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                    <InfoField label="Proyecto" value={editMode === 'ubicacion' ?
                      <Input className="h-8 text-sm" value={editForm.proyecto_nombre || ''} onChange={e => setEditForm(p => ({...p, proyecto_nombre: e.target.value}))} /> :
                      entrega.proyecto_nombre} />
                    {(editMode === 'ubicacion' ? etapasEdicion.etapas.length > 0 : !!entrega.subdivision_nombre) && (
                      <InfoField label="Etapa" value={editMode === 'ubicacion' ?
                        <EtapaSelect etapas={etapasEdicion.etapas} value={editForm.subdivision_id} className="h-8 text-sm"
                          testId="select-etapa-detalle"
                          onChange={e => setEditForm(p => ({ ...p, subdivision_id: e?.id, subdivision_nombre: e?.nombre }))} /> :
                        <span className="inline-flex items-center gap-1.5" data-testid="detalle-etapa">
                          {entrega.subdivision_nombre}
                          {!entrega.subdivision_id && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-red-300 text-red-600 bg-red-50"
                              title="La etapa no está vinculada: edite Ubicación y seleccione la etapa" data-testid="badge-etapa-sin-vinculo">
                              sin vínculo
                            </Badge>
                          )}
                        </span>} />
                    )}
                    <InfoField label="Proforma" value={editMode === 'ubicacion' ?
                      <Input className="h-8 text-sm" value={editForm.proforma_codigo || ''} onChange={e => setEditForm(p => ({...p, proforma_codigo: e.target.value}))} /> :
                      <span className="inline-flex items-center gap-1.5">
                        {entrega.proforma_codigo || entrega.sperant_proforma_id}
                        {['Liberación', 'Inspección'].includes(entrega.fase) && !entrega.fecha_entrega_contractual && !entrega.fecha_entrega_reprogramada && editMode !== 'ubicacion' && (
                          <Button
                            variant="ghost" size="sm"
                            className="h-6 w-6 p-0 text-[#4E7A79] hover:text-[#3a5e5d] hover:bg-[#F0F7F6]"
                            data-testid="btn-refresh-crm"
                            title="Actualizar datos desde CRM"
                            onClick={handleRefreshCRM}
                            disabled={refreshingCRM}
                          >
                            {refreshingCRM ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                          </Button>
                        )}
                      </span>} />
                    <InfoField label="Tipo Unidad" value={getUnidadTipo(entrega)} />
                    <InfoField label="Unidad Principal" value={getUnidadCodigo(entrega)} />
                    <InfoField label="Unidades Asignadas" value={
                      entrega.unidades_asignadas && entrega.unidades_asignadas.length > 0
                        ? entrega.unidades_asignadas.map(u => formatUnidadAsignada(u)).join(', ')
                        : '-'
                    } />
                    <InfoField label="Fecha Contractual" value={editMode === 'ubicacion' ?
                      <Input type="date" className="h-8 text-sm" value={editForm.fecha_entrega_contractual || ''} onChange={e => setEditForm(p => ({...p, fecha_entrega_contractual: e.target.value}))} /> :
                      entrega.calculando_fechas ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Loader2 size={12} className="animate-spin text-amber-600" />
                          <Badge variant="outline" className="text-xs border-amber-400 text-amber-700 bg-amber-50 animate-pulse" data-testid="badge-calculando-contractual">
                            Calculando...
                          </Badge>
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5">
                          {entrega.fecha_entrega_contractual || '-'}
                          {esOrigenCrm && entrega.fecha_entrega_contractual && docsEfectivos.contrato?.url && (
                            <Button
                              variant="ghost" size="sm"
                              className="h-6 w-6 p-0 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                              data-testid="btn-ver-contrato"
                              title={`Ver ${docsEfectivos.contrato.nombre || 'contrato'}`}
                              onClick={() => setPdfPreview({
                                url: docsEfectivos.contrato.url,
                                nombre: docsEfectivos.contrato.nombre || 'Contrato de Compraventa'
                              })}
                            >
                              <FileText size={13} />
                            </Button>
                          )}
                          {entrega.calculando_fechas_error && !entrega.calculando_fechas && esOrigenCrm && (
                            <Button
                              variant="ghost" size="sm"
                              className="h-6 w-6 p-0 text-amber-600 hover:text-amber-800 hover:bg-amber-50"
                              data-testid="btn-reintentar-extraccion"
                              title="Reintentar extracción de fechas desde PDFs"
                              onClick={async () => {
                                try {
                                  await api.post(`/entregas/${id}/reintentar-extraccion`);
                                  toast.success('Reintentando extracción de fechas...');
                                  setEntrega(prev => ({ ...prev, calculando_fechas: true, calculando_fechas_error: null }));
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || 'Error al reintentar');
                                }
                              }}
                            >
                              <RefreshCw size={13} />
                            </Button>
                          )}
                        </span>
                      )} />
                    <InfoField label="Fecha Reprogramada" value={
                      entrega.calculando_fechas ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Loader2 size={12} className="animate-spin text-amber-600" />
                          <Badge variant="outline" className="text-xs border-amber-400 text-amber-700 bg-amber-50 animate-pulse" data-testid="badge-calculando-reprogramada">
                            Calculando...
                          </Badge>
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5">
                          {entrega.fecha_entrega_reprogramada || '-'}
                          {(() => {
                            if (!esOrigenCrm || !entrega.fecha_entrega_reprogramada) return null;
                            const adendas = docsEfectivos.adendas.filter(d => d.url);
                            if (adendas.length === 0) return null;
                            if (adendas.length === 1) {
                              return (
                                <Button
                                  variant="ghost" size="sm"
                                  className="h-6 w-6 p-0 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                  data-testid="btn-ver-adenda"
                                  title={`Ver ${adendas[0].nombre || 'documento'}`}
                                  onClick={() => setPdfPreview({
                                    url: adendas[0].url,
                                    nombre: adendas[0].nombre || 'Documento posterior al contrato'
                                  })}
                                >
                                  <FileText size={13} />
                                </Button>
                              );
                            }
                            return (
                              <Button
                                variant="ghost" size="sm"
                                className="h-6 w-6 p-0 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                data-testid="btn-ver-adendas"
                                title="Ver documentos posteriores al contrato"
                                onClick={() => setShowDocsList(true)}
                              >
                                <FileText size={13} />
                              </Button>
                            );
                          })()}
                        </span>
                      )} />
                    {entrega.calculando_fechas_error && !entrega.calculando_fechas && (
                      <div className="col-span-2 sm:col-span-3">
                        <Badge variant="destructive" className="text-xs" data-testid="badge-error-calculo">
                          Error al extraer fechas: {entrega.calculando_fechas_error.substring(0, 250)}
                        </Badge>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Responsable */}
              <Card className="border border-[#E8E8E7]">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold text-[#4E7A79]">Responsable</CardTitle>
                    {currentUser?.rol_nombre === 'Administrador' && entrega.arquitecto_nombre && entrega.estado !== 'sin_arquitecto_asignado' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={openAsignarArquitecto}
                        className="h-7 text-xs text-[#9d8272]"
                        data-testid="btn-cambiar-responsable"
                      >
                        <Edit2 size={12} className="mr-1" /> Cambiar
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2 text-sm">
                    <User size={14} className="text-[#888]" />
                    <span className={entrega.arquitecto_nombre ? 'text-[#2D2D2D]' : 'text-red-400'}>
                      {entrega.arquitecto_nombre || 'Sin arquitecto asignado'}
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Cliente Titular */}
              <Card className="border border-[#E8E8E7]">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold text-[#4E7A79]">Cliente Titular</CardTitle>
                    {!datosCRMRefresh && (
                      editMode !== 'cliente' ? (
                        <Button variant="ghost" size="sm" onClick={() => startEdit('cliente')} className="h-7 text-xs text-[#9d8272]">
                          <Edit2 size={12} className="mr-1" /> Editar
                        </Button>
                      ) : (
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={cancelEdit} className="h-7 text-xs"><X size={12} /></Button>
                          <Button size="sm" onClick={saveEdit} disabled={saving} className="h-7 text-xs bg-[#4E7A79] text-white">
                            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} className="mr-1" />} Guardar
                          </Button>
                        </div>
                      )
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                    <InfoField label="Nombre" value={editMode === 'cliente' || datosCRMRefresh ?
                      <Input className="h-8 text-sm" value={editForm.cliente_nombre || ''} onChange={e => setEditForm(p => ({...p, cliente_nombre: e.target.value}))} /> :
                      entrega.cliente_nombre} />
                    <InfoField label="Tipo Doc." value={editMode === 'cliente' || datosCRMRefresh ?
                      <Select value={editForm.cliente_tipo_documento || ''} onValueChange={v => setEditForm(p => ({...p, cliente_tipo_documento: v}))}>
                        <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Tipo" /></SelectTrigger>
                        <SelectContent>{TIPOS_DOCUMENTO.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select> :
                      entrega.cliente_tipo_documento || '-'} />
                    <InfoField label="N° Doc." value={editMode === 'cliente' || datosCRMRefresh ?
                      <Input className="h-8 text-sm" value={editForm.cliente_numero_documento || ''} onChange={e => setEditForm(p => ({...p, cliente_numero_documento: e.target.value}))} /> :
                      entrega.cliente_numero_documento || '-'} />
                    <InfoField label="Email" value={editMode === 'cliente' || datosCRMRefresh ?
                      <Input type="email" className="h-8 text-sm" value={editForm.cliente_email || ''} onChange={e => setEditForm(p => ({...p, cliente_email: e.target.value}))} /> :
                      entrega.cliente_email || '-'} />
                    <InfoField label="Celular" value={editMode === 'cliente' || datosCRMRefresh ?
                      <Input className="h-8 text-sm" value={editForm.cliente_celular || ''} onChange={e => setEditForm(p => ({...p, cliente_celular: e.target.value}))} /> :
                      entrega.cliente_celular || '-'} />
                    <InfoField label="Tel. Fijo" value={editMode === 'cliente' || datosCRMRefresh ?
                      <Input className="h-8 text-sm" value={editForm.cliente_telefono_fijo || ''} onChange={e => setEditForm(p => ({...p, cliente_telefono_fijo: e.target.value}))} /> :
                      entrega.cliente_telefono_fijo || '-'} />
                  </div>
                </CardContent>
              </Card>

              {/* Copropietarios — siempre visible */}
              <Card className="border border-[#E8E8E7]" data-testid="card-copropietarios">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold text-[#4E7A79]">Copropietarios</CardTitle>
                    {!datosCRMRefresh && (
                      editMode !== 'copropietarios' ? (
                        <Button variant="ghost" size="sm" onClick={() => startEdit('copropietarios')} className="h-7 text-xs text-[#9d8272]" data-testid="btn-edit-copropietarios">
                          <Edit2 size={12} className="mr-1" /> Editar
                        </Button>
                      ) : (
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={cancelEdit} className="h-7 text-xs"><X size={12} /></Button>
                          <Button size="sm" onClick={saveEdit} disabled={saving} className="h-7 text-xs bg-[#4E7A79] text-white" data-testid="btn-save-copropietarios">
                            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} className="mr-1" />} Guardar
                          </Button>
                        </div>
                      )
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {editMode === 'copropietarios' ? (
                    <div className="space-y-3" data-testid="copropietarios-edit">
                      {(editForm.co_propietarios || []).map((cp, idx) => (
                        <div key={idx} className="border border-[#E8E8E7] rounded p-3 relative" data-testid={`coprop-edit-row-${idx}`}>
                          <button
                            onClick={() => setEditForm(prev => ({
                              ...prev,
                              co_propietarios: prev.co_propietarios.filter((_, i) => i !== idx)
                            }))}
                            className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded hover:bg-red-50 text-[#999] hover:text-red-500 transition-colors"
                            data-testid={`btn-remove-coprop-${idx}`}
                          >
                            <X size={14} />
                          </button>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                            <div>
                              <Label className="text-[10px] text-[#888] uppercase">Nombre</Label>
                              <Input className="h-8 text-sm mt-0.5" value={cp.nombre || ''} onChange={e => {
                                const val = e.target.value;
                                setEditForm(prev => ({
                                  ...prev,
                                  co_propietarios: prev.co_propietarios.map((c, i) => i === idx ? { ...c, nombre: val } : c)
                                }));
                              }} data-testid={`coprop-nombre-${idx}`} />
                            </div>
                            <div>
                              <Label className="text-[10px] text-[#888] uppercase">Tipo Doc.</Label>
                              <Select value={cp.tipo_documento || 'DNI'} onValueChange={val => {
                                setEditForm(prev => ({
                                  ...prev,
                                  co_propietarios: prev.co_propietarios.map((c, i) => i === idx ? { ...c, tipo_documento: val } : c)
                                }));
                              }}>
                                <SelectTrigger className="h-8 text-sm mt-0.5" data-testid={`coprop-tipodoc-${idx}`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {TIPOS_DOCUMENTO.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-[10px] text-[#888] uppercase">N° Doc.</Label>
                              <Input className="h-8 text-sm mt-0.5" value={cp.numero_documento || ''} onChange={e => {
                                const val = e.target.value;
                                setEditForm(prev => ({
                                  ...prev,
                                  co_propietarios: prev.co_propietarios.map((c, i) => i === idx ? { ...c, numero_documento: val } : c)
                                }));
                              }} data-testid={`coprop-numdoc-${idx}`} />
                            </div>
                            <div>
                              <Label className="text-[10px] text-[#888] uppercase">Email</Label>
                              <Input className="h-8 text-sm mt-0.5" type="email" value={cp.email || ''} onChange={e => {
                                const val = e.target.value;
                                setEditForm(prev => ({
                                  ...prev,
                                  co_propietarios: prev.co_propietarios.map((c, i) => i === idx ? { ...c, email: val } : c)
                                }));
                              }} data-testid={`coprop-email-${idx}`} />
                            </div>
                            <div>
                              <Label className="text-[10px] text-[#888] uppercase">Teléfono</Label>
                              <Input className="h-8 text-sm mt-0.5" value={cp.telefono || ''} onChange={e => {
                                const val = e.target.value;
                                setEditForm(prev => ({
                                  ...prev,
                                  co_propietarios: prev.co_propietarios.map((c, i) => i === idx ? { ...c, telefono: val } : c)
                                }));
                              }} data-testid={`coprop-telefono-${idx}`} />
                            </div>
                          </div>
                        </div>
                      ))}
                      <Button
                        variant="outline" size="sm"
                        className="text-xs border-dashed border-[#4E7A79] text-[#4E7A79]"
                        onClick={() => setEditForm(prev => ({
                          ...prev,
                          co_propietarios: [...(prev.co_propietarios || []), { nombre: '', tipo_documento: 'DNI', numero_documento: '', email: '', telefono: '' }]
                        }))}
                        data-testid="btn-add-copropietario"
                      >
                        <UserPlus size={12} className="mr-1" /> Agregar Copropietario
                      </Button>
                    </div>
                  ) : (
                    <div>
                      {((datosCRMRefresh ? editForm.co_propietarios : entrega.co_propietarios) || []).length > 0 ? (
                        <div className="space-y-3">
                          {(datosCRMRefresh ? editForm.co_propietarios : entrega.co_propietarios).map((cp, idx) => (
                            <div key={idx} className="border border-[#E8E8E7] rounded p-2 text-sm" data-testid={`coprop-row-${idx}`}>
                              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                <InfoField label="Nombre" value={cp.nombre} />
                                <InfoField label="Tipo Doc." value={cp.tipo_documento || '-'} />
                                <InfoField label="N° Doc." value={cp.numero_documento || '-'} />
                                <InfoField label="Email" value={cp.email || '-'} />
                                <InfoField label="Teléfono" value={cp.telefono || '-'} />
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-[#aaa] italic" data-testid="coprop-empty">No se han registrado copropietarios.</p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Premio + Notas */}
              <Card className="border border-[#E8E8E7]">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold text-[#4E7A79]">Adicional</CardTitle>
                    {!datosCRMRefresh && (
                      editMode !== 'extra' ? (
                        <Button variant="ghost" size="sm" onClick={() => startEdit('extra')} className="h-7 text-xs text-[#9d8272]">
                          <Edit2 size={12} className="mr-1" /> Editar
                        </Button>
                      ) : (
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={cancelEdit} className="h-7 text-xs"><X size={12} /></Button>
                          <Button size="sm" onClick={saveEdit} disabled={saving} className="h-7 text-xs bg-[#4E7A79] text-white">
                            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} className="mr-1" />} Guardar
                          </Button>
                        </div>
                      )
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <InfoField label="Premio" value={editMode === 'extra' || datosCRMRefresh ?
                      <Input className="h-8 text-sm" value={editForm.premio || ''} onChange={e => setEditForm(p => ({...p, premio: e.target.value}))} /> :
                      entrega.premio || '-'} />
                    <InfoField label="Origen" value={datosCRMRefresh ? (
                      <span className="inline-flex items-center gap-1.5 text-sm">
                        <span className="text-muted-foreground line-through">{entrega.origen}</span>
                        <span className="text-[#4E7A79]">→ crm</span>
                      </span>
                    ) : entrega.origen} />
                    <div className="sm:col-span-2">
                      <InfoField label="Notas" value={editMode === 'extra' || datosCRMRefresh ?
                        <Textarea className="text-sm" rows={2} value={editForm.notas || ''} onChange={e => setEditForm(p => ({...p, notas: e.target.value}))} /> :
                        entrega.notas || '-'} />
                    </div>
                  </div>
                </CardContent>
              </Card>


              {/* Banner comunicación unificada */}
              {progConfirmada?.entregas_vinculadas_ids && progConfirmada.entregas_vinculadas_ids.length > 0 && (
                <Card className="border border-blue-200 bg-blue-50/30" data-testid="banner-comunicacion-unificada">
                  <CardContent className="py-3 px-4">
                    <div className="flex items-center gap-2 text-sm text-blue-700">
                      <Link2 size={14} />
                      <span className="font-semibold">Comunicación unificada</span>
                    </div>
                    {progConfirmada.entrega_principal_codigo ? (
                      <p className="text-xs text-blue-600 mt-1">
                        Esta entrega hereda programación de: <strong>{progConfirmada.entrega_principal_codigo}</strong>
                      </p>
                    ) : (
                      <p className="text-xs text-blue-600 mt-1">
                        Esta entrega comparte programación con: <strong>{(progConfirmada.entregas_vinculadas_codigos || progConfirmada.entregas_vinculadas_ids).join(', ')}</strong>
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Cita Confirmada - Info */}
              {entrega.estado === 'fecha_confirmada' && entrega.fecha_cita_confirmada && (
                <Card className="border border-green-200 bg-green-50/30" data-testid="info-cita-confirmada">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm font-semibold text-green-700 flex items-center gap-1.5">
                        <CheckCircle2 size={14} /> Cita Confirmada
                      </CardTitle>
                      <button
                        onClick={openReprogramarCita}
                        className="p-1.5 rounded-md hover:bg-green-100 text-green-600 hover:text-green-800 transition-colors"
                        title="Reprogramar cita"
                        data-testid="btn-reprogramar-cita"
                      >
                        <Edit2 size={14} />
                      </button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <p className="text-sm text-[#2D2D2D]">
                      <strong>Fecha:</strong> {(() => {
                        const fc = entrega.fecha_cita_confirmada;
                        const datePart = fc.substring(0, 10);
                        const timePart = fc.substring(11, 16);
                        const [y, m, d] = datePart.split('-');
                        const dias = ['domingo','lunes','martes','miércoles','jueves','viernes','sábado'];
                        const meses = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','setiembre','octubre','noviembre','diciembre'];
                        const dt = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
                        return `${dias[dt.getDay()]}, ${parseInt(d)} de ${meses[parseInt(m) - 1]} de ${y}, ${timePart}`;
                      })()}
                    </p>
                    {progConfirmada?.confirmante_nombre && (
                      <div className="text-xs text-[#555] space-y-0.5" data-testid="info-confirmante">
                        <p><strong>Confirmado por:</strong> {progConfirmada.confirmante_nombre}{progConfirmada.confirmante_email ? ` (${progConfirmada.confirmante_email})` : ''}</p>
                        {progConfirmada.confirmado_at && (
                          <p><strong>Fecha de confirmación:</strong> {new Date(progConfirmada.confirmado_at).toLocaleString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                        )}
                      </div>
                    )}
                    {progConfirmada?.invitados_confirmacion && progConfirmada.invitados_confirmacion.length > 0 && (
                      <div className="mt-1" data-testid="info-invitados-confirmacion">
                        <p className="text-xs font-semibold text-[#888] mb-1">Invitados:</p>
                        <div className="border border-[#e0e0e0] rounded overflow-hidden">
                          {progConfirmada.invitados_confirmacion.map((inv, i) => (
                            <div key={i} className="flex justify-between text-xs px-3 py-1.5 border-b border-[#f0f0f0] last:border-b-0">
                              <span className="text-[#333]">{inv.nombre}</span>
                              <span className="text-[#888]">{inv.documento}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Autorizaciones */}
                    {(progConfirmada.autoriza_imagen != null || progConfirmada.autoriza_datos != null) && (
                      <div className="mt-3 pt-3 border-t border-[#E8E8E7]">
                        <p className="text-xs font-semibold text-[#5A5A5A] mb-2">Autorizaciones del Cliente</p>
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-2 text-xs" data-testid="autoriza-imagen-display">
                            {progConfirmada.autoriza_imagen
                              ? <CheckCircle size={14} className="text-green-500 shrink-0" />
                              : <XCircle size={14} className="text-[#aaa] shrink-0" />}
                            <span className="text-[#5A5A5A]">Uso de imagen con fines institucionales</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs" data-testid="autoriza-datos-display">
                            {progConfirmada.autoriza_datos
                              ? <CheckCircle size={14} className="text-green-500 shrink-0" />
                              : <XCircle size={14} className="text-[#aaa] shrink-0" />}
                            <span className="text-[#5A5A5A]">Tratamiento de datos personales</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Citación Notarial - Info */}
              {entrega.estado === 'citacion_notarial' && (
                <CitacionNotarialInfo entregaId={id} codigo={entrega.codigo} />
              )}

              {/* Metadata */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-[#888]">
                <span>Creado: {entrega.created_at ? new Date(entrega.created_at).toLocaleString('es-PE') : '-'}</span>
                <span>Actualizado: {entrega.updated_at ? new Date(entrega.updated_at).toLocaleString('es-PE') : '-'}</span>
                <span>Origen: {entrega.origen}</span>
              </div>
            </TabsContent>

            {/* Tab: Checklist Pre-Entrega */}
            <TabsContent value="checklist" className="mt-4">
              <ChecklistPreEntrega
                entregaId={id}
                fase={entrega.fase}
                currentUser={currentUser}
                tieneArquitecto={!!entrega.arquitecto_id}
                onGenerado={loadHistorial}
              />
            </TabsContent>

            {/* Tab: Historial */}
            <TabsContent value="historial" className="mt-4">
              <Card className="border border-[#E8E8E7]">
                <CardContent className="p-0">
                  {/* Sección: Transiciones de Estado */}
                  <div className="flex items-center gap-2 px-4 pt-3 pb-2 border-b border-[#f0f0f0]">
                    <History size={14} className="text-[#5A5A5A]" />
                    <span className="text-[13px] font-semibold text-[#2D2D2D]">Transiciones de Estado</span>
                    <span className="ml-auto text-[11px] text-[#888]">{historial.length} transiciones</span>
                  </div>
                  {historial.length === 0 ? (
                    <div className="text-center py-12">
                      <History size={32} className="mx-auto text-[#ccc] mb-2" />
                      <p className="text-sm text-[#888]">No hay transiciones registradas</p>
                    </div>
                  ) : (
                    <table className="w-full text-sm" data-testid="tabla-historial">
                      <thead>
                        <tr className="border-b border-[#E8E8E7] bg-[#FAFAFA]">
                          <th className="text-left p-3 font-medium text-[#5A5A5A]">Fecha</th>
                          <th className="text-left p-3 font-medium text-[#5A5A5A]">Transición</th>
                          <th className="text-left p-3 font-medium text-[#5A5A5A] hidden sm:table-cell">Fase</th>
                          <th className="text-left p-3 font-medium text-[#5A5A5A] hidden md:table-cell">Usuario</th>
                          <th className="text-left p-3 font-medium text-[#5A5A5A] hidden lg:table-cell">Notas</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historial.map((h) => (
                          <tr key={h.id} className="border-b border-[#E8E8E7]">
                            <td className="p-3 text-xs text-[#888]">
                              <Clock size={12} className="inline mr-1" />
                              {new Date(h.created_at).toLocaleString('es-PE')}
                            </td>
                            <td className="p-3">
                              <span className="text-xs">
                                <span style={{ color: getEstadoColor(h.estado_anterior) }}>
                                  {getEstadoLabel(h.estado_anterior)}
                                </span>
                                {' → '}
                                <span className="font-medium" style={{ color: getEstadoColor(h.estado_nuevo) }}>
                                  {getEstadoLabel(h.estado_nuevo)}
                                </span>
                              </span>
                            </td>
                            <td className="p-3 hidden sm:table-cell text-xs">{h.fase_nueva}</td>
                            <td className="p-3 hidden md:table-cell text-xs text-[#5A5A5A]">{h.usuario_nombre || '-'}</td>
                            <td className="p-3 hidden lg:table-cell text-xs text-[#888]">
                              {(() => {
                                const audit = auditConfirmaciones.find(a => a.historial_id === h.id);
                                if (audit) {
                                  return (
                                    <button
                                      onClick={() => setAuditModal(audit)}
                                      className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 hover:bg-amber-100 transition-colors"
                                      data-testid={`btn-audit-${h.id}`}
                                    >
                                      <FileText size={12} />
                                      Ver auditoría
                                    </button>
                                  );
                                }
                                return h.notas || '-';
                              })()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  {/* Divisor */}
                  <hr className="border-t border-dashed border-[#d4c5bb] mx-4 my-4" />

                  {/* Sección: Correos Enviados */}
                  <div className="flex items-center gap-2 px-4 pb-2">
                    <Mail size={14} className="text-[#5B7FA6]" />
                    <span className="text-[13px] font-semibold text-[#2D2D2D]">Correos Enviados</span>
                    <span className="ml-auto text-[11px] text-[#888]">{emailLogs.length} correos</span>
                  </div>

                  {emailLogs.length === 0 ? (
                    <div className="text-center py-8 px-4">
                      <Mail size={28} className="mx-auto text-[#ccc] mb-2" />
                      <p className="text-sm text-[#888]">No se han enviado correos para esta entrega</p>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2 px-4 pb-3" data-testid="email-logs-list">
                      {emailLogs.map((log) => {
                        const isReprog = (log.subject || '').toLowerCase().includes('reprogramación') || (log.subject || '').toLowerCase().includes('solicitada');
                        return (
                          <div key={log.id} className="flex items-center gap-3 p-3 rounded-lg border border-[#e8e8e8] bg-[#fafafa]" data-testid={`email-log-${log.id}`}>
                            <div className={`w-[34px] h-[34px] rounded-lg flex items-center justify-center shrink-0 ${isReprog ? 'bg-orange-50' : 'bg-blue-50'}`}>
                              <Send size={14} className={isReprog ? 'text-orange-500' : 'text-[#5B7FA6]'} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs font-semibold text-[#2D2D2D]">
                                  {log.subject || 'Correo enviado'}
                                </span>
                                <Badge className="text-[9px] px-1.5 py-0 h-4 border-0 bg-green-100 text-green-700 font-semibold">
                                  ENVIADO
                                </Badge>
                              </div>
                              <div className="flex gap-3 mt-0.5 text-[11px] text-[#888] flex-wrap">
                                <span>{log.recipient_email}</span>
                                {log.cc_emails && log.cc_emails.length > 0 && (
                                  <span className="text-[#aaa]">CC: {log.cc_emails.join(', ')}</span>
                                )}
                                <span>{new Date(log.sent_at).toLocaleString('es-PE')}</span>
                                {log.sent_by_name && <span>{log.sent_by_name}</span>}
                              </div>
                            </div>
                            <div className="flex gap-1.5 shrink-0">
                              <button
                                onClick={() => setEmailPreview(log)}
                                className="inline-flex items-center gap-1 text-[11px] font-medium text-[#9d8272] bg-[#faf8f6] border border-[#d4c5bb] rounded-md px-2.5 py-1.5 hover:bg-[#f0ebe5] transition-colors"
                                data-testid={`btn-ver-correo-${log.id}`}
                              >
                                <Eye size={12} />
                                Ver correo
                              </button>
                              {log.resend_email_id && (
                                <a
                                  href={`https://resend.com/emails/${log.resend_email_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md px-2.5 py-1.5 hover:bg-blue-100 transition-colors"
                                  data-testid={`btn-resend-${log.id}`}
                                >
                                  <ExternalLink size={12} />
                                  Resend
                                </a>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <p className="text-[10px] text-[#aaa] px-4 pb-3">
                    El link "Resend" expira a los 30 días. "Ver correo" es una copia local permanente.
                  </p>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab: Datos Import */}
            {entrega.datos_sperant && (
              <TabsContent value="import" className="mt-4">
                <Card className="border border-[#E8E8E7]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold text-[#4E7A79]">Datos del Excel de Importación</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {entrega.datos_sperant.archivo && (
                      <p className="text-xs text-gray-500 mb-3">
                        Archivo: <span className="font-medium">{entrega.datos_sperant.archivo}</span>
                        {entrega.datos_sperant.fila && <> &middot; Fila: <span className="font-medium">{entrega.datos_sperant.fila}</span></>}
                      </p>
                    )}
                    {!entrega.datos_sperant.archivo && entrega.datos_sperant.import_fila && (
                      <p className="text-xs text-gray-500 mb-3">
                        Fila de importación: <span className="font-medium">{entrega.datos_sperant.import_fila}</span>
                        <span className="ml-2 italic">(importado antes de la captura completa de datos)</span>
                      </p>
                    )}
                    {(() => {
                      const entries = Object.entries(entrega.datos_sperant)
                        .filter(([k]) => !['archivo', 'fila', 'import_fila'].includes(k));
                      return entries.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {entries.map(([key, val]) => (
                            <div key={key} className="bg-[#F8F8F8] rounded p-2">
                              <p className="text-[10px] text-gray-500 uppercase tracking-wide">{key}</p>
                              <p className="text-sm font-medium text-gray-800 break-words">{val || '-'}</p>
                            </div>
                          ))}
                        </div>
                      ) : null;
                    })()}
                  </CardContent>
                </Card>
              </TabsContent>
            )}

            {/* Tab: CRM */}
            {esOrigenCrm && (
              <TabsContent value="crm" className="mt-4 space-y-4" data-testid="tab-crm-content">
                {/* Estado CRM */}
                <Card className="border border-[#E8E8E7]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold text-[#4E7A79]">Estado CRM</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      <InfoField label="Proyecto CRM" value={entrega.crm_proyecto_codigo || '-'} />
                      <InfoField label="Estado Comercial" value={entrega.crm_estado_comercial || '-'} />
                      <InfoField label="Estado Personalizado" value={entrega.crm_estado_personalizado || '-'} />
                      <InfoField label="Flujo de Proceso" value={entrega.crm_flujo_proceso || '-'} />
                      <InfoField label="Creado por" value={entrega.crm_creado_por || '-'} />
                      <InfoField label="Fecha Creación CRM" value={entrega.crm_fecha_creacion || '-'} />
                      <InfoField label="Estado Venta" value={entrega.crm_estado_venta || '-'} />
                      <InfoField label="Fecha Cierre Venta" value={entrega.crm_fecha_cierre_venta || '-'} />
                      <InfoField label="Consultado" value={entrega.crm_consultado_at ? new Date(entrega.crm_consultado_at).toLocaleString('es-PE') : '-'} />
                    </div>
                  </CardContent>
                </Card>

                {/* Documentos PDF */}
                <DocumentosCRMCard entrega={entrega} />

                {/* Raw CRM Payload */}
                <Card className="border border-[#E8E8E7]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold text-[#4E7A79]">Payload CRM (raw)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <pre className="text-xs bg-[#F8F8F8] p-3 rounded overflow-x-auto max-h-[400px]">
                      {JSON.stringify(entrega.crm_raw_payload, null, 2)}
                    </pre>
                  </CardContent>
                </Card>
              </TabsContent>
            )}
          </Tabs>
        </div>
      </div>

      {/* Dialog: Asignar Arquitecto */}
      <Dialog open={showAsignarArq} onOpenChange={setShowAsignarArq}>
        <DialogContent className="max-w-sm" data-testid="dialog-asignar-arquitecto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus size={18} className="text-[#4E7A79]" />
              Asignar Arquitecto Responsable
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {loadingArqs ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 size={20} className="animate-spin text-[#4E7A79]" />
              </div>
            ) : arquitectosDisponibles.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-sm text-[#888] mb-2">No hay usuarios asignados al área "Entregas".</p>
                <p className="text-xs text-orange-500">Ve a Usuarios &gt; Áreas Funcionales para asignar usuarios al área Entregas.</p>
              </div>
            ) : (
              <div>
                <Label className="text-sm">Arquitecto <span className="text-xs text-[#888]">(área Entregas)</span></Label>
                <Select value={selArquitectoId} onValueChange={setSelArquitectoId}>
                  <SelectTrigger className="mt-1" data-testid="sel-arquitecto-asignar">
                    <SelectValue placeholder="Seleccionar arquitecto" />
                  </SelectTrigger>
                  <SelectContent>
                    {arquitectosDisponibles.map(a => (
                      <SelectItem key={a.id} value={a.id}>{a.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowAsignarArq(false)}>Cancelar</Button>
              <Button
                onClick={handleAsignarArquitecto}
                disabled={asignando || !selArquitectoId || arquitectosDisponibles.length === 0}
                className="bg-[#4E7A79] hover:bg-[#3d6362] text-white"
                data-testid="btn-confirmar-asignar-arq"
              >
                {asignando ? <Loader2 size={14} className="animate-spin mr-1" /> : <UserPlus size={14} className="mr-1" />}
                Asignar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog: WIP - Iniciar Entrega */}
      <Dialog open={showWipModal} onOpenChange={setShowWipModal}>
        <DialogContent className="max-w-sm" data-testid="dialog-wip">
          <DialogHeader>
            <DialogTitle className="text-center text-base font-semibold text-[#333]">Iniciar Entrega</DialogTitle>
          </DialogHeader>
          <div className="text-center py-4">
            <Construction size={40} className="mx-auto text-amber-500 mb-3" />
            <p className="text-sm text-[#656C78]">En proceso de implementación...</p>
          </div>
          <div className="flex justify-center">
            <Button onClick={() => setShowWipModal(false)} className="bg-[#4E7A79] hover:bg-[#3d6160] text-white" data-testid="btn-wip-aceptar">
              Aceptar
            </Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* Dialog: Reprogramar Cita Confirmada */}
      <Dialog open={showReprogramar} onOpenChange={setShowReprogramar}>
        <DialogContent className="max-w-md" data-testid="dialog-reprogramar-cita">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#4E7A79]">
              <CalendarDays size={18} />
              Reprogramar Cita Confirmada
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {entrega?.fecha_cita_confirmada && (
              <div className="p-2 bg-[#F5F5F4] rounded-lg border border-[#E8E8E7]">
                <p className="text-xs text-[#888]">Fecha actual</p>
                <p className="text-sm font-medium text-[#2D2D2D]">
                  {new Date(entrega.fecha_cita_confirmada).toLocaleString('es-PE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            )}
            <div>
              <Label className="text-sm">Nueva fecha <span className="text-red-500">*</span></Label>
              <Input
                type="date"
                value={reprogForm.fecha}
                onChange={e => setReprogForm(f => ({ ...f, fecha: e.target.value }))}
                className="mt-1"
                data-testid="input-reprog-fecha"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Hora inicio <span className="text-red-500">*</span></Label>
                <Input
                  type="time"
                  value={reprogForm.hora_inicio}
                  onChange={e => setReprogForm(f => ({ ...f, hora_inicio: e.target.value }))}
                  className="mt-1"
                  data-testid="input-reprog-hora-inicio"
                />
              </div>
              <div>
                <Label className="text-sm">Hora fin <span className="text-red-500">*</span></Label>
                <Input
                  type="time"
                  value={reprogForm.hora_fin}
                  onChange={e => setReprogForm(f => ({ ...f, hora_fin: e.target.value }))}
                  className="mt-1"
                  data-testid="input-reprog-hora-fin"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm">Motivo de reprogramación <span className="text-red-500">*</span></Label>
              <textarea
                value={reprogForm.motivo}
                onChange={e => setReprogForm(f => ({ ...f, motivo: e.target.value }))}
                className="w-full mt-1 p-2 border border-[#E8E8E7] rounded-md text-sm resize-none focus:outline-none focus:ring-1 focus:ring-[#4E7A79]"
                rows={3}
                placeholder="Ej: El cliente no podrá asistir por viaje de trabajo"
                data-testid="input-reprog-motivo"
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setShowReprogramar(false)}>Cancelar</Button>
              <Button
                onClick={handleReprogramarCita}
                disabled={savingReprog}
                className="bg-[#4E7A79] hover:bg-[#3d6362] text-white"
                data-testid="btn-guardar-reprog"
              >
                {savingReprog ? <Loader2 size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1" />}
                Guardar cambio
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: Preview correo enviado */}
      <Dialog open={!!emailPreview} onOpenChange={() => setEmailPreview(null)}>
        <DialogContent className="sm:max-w-[760px] p-0 overflow-hidden" data-testid="modal-email-preview">
          <DialogHeader className="px-5 pt-4 pb-3 border-b border-[#e8e8e8]">
            <DialogTitle className="text-sm flex items-center gap-2">
              <Mail size={16} className="text-[#9d8272]" />
              Correo Enviado
            </DialogTitle>
          </DialogHeader>
          {emailPreview && (
            <>
              <div className="bg-[#f5f5f5]" style={{ height: '500px' }}>
                <iframe
                  id="email-preview-iframe"
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/email-audit/${emailPreview.id}/preview`}
                  title="Preview correo"
                  className="w-full h-full border-none"
                  data-testid="iframe-email-preview"
                />
              </div>
              <div className="flex items-center justify-between px-5 py-3 border-t border-[#e8e8e8]">
                <p className="text-[10px] text-[#aaa]">Copia local permanente del correo enviado</p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      const iframe = document.getElementById('email-preview-iframe');
                      if (iframe && iframe.contentWindow) iframe.contentWindow.print();
                    }}
                    className="inline-flex items-center gap-1 text-[11px] font-medium text-[#9d8272] bg-[#faf8f6] border border-[#d4c5bb] rounded-md px-2.5 py-1.5 hover:bg-[#f0ebe5] transition-colors"
                    data-testid="btn-print-email"
                  >
                    <Printer size={12} />
                    Imprimir
                  </button>
                  {emailPreview.resend_email_id && (
                    <a
                      href={`https://resend.com/emails/${emailPreview.resend_email_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md px-2.5 py-1.5 hover:bg-blue-100 transition-colors"
                      data-testid="modal-resend-link"
                    >
                      <ExternalLink size={12} />
                      Ver en Resend
                    </a>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal: Auditoría anti-repudio */}
      <Dialog open={!!auditModal} onOpenChange={() => setAuditModal(null)}>
        <DialogContent className="sm:max-w-[600px] p-0 overflow-hidden" data-testid="modal-audit-confirmacion">
          <DialogHeader className="px-5 pt-4 pb-3 border-b border-[#e8e8e8]">
            <DialogTitle className="text-sm flex items-center gap-2">
              <FileText size={16} className="text-amber-600" />
              Auditoría de Confirmación
            </DialogTitle>
          </DialogHeader>
          {auditModal && (() => {
            const meta = auditModal.metadata || {};
            const tipoLabels = {
              confirmacion_cita: 'Confirmación de Cita',
              firma_acta_entrega: 'Firma de Acta de Entrega',
              aceptacion_observaciones: 'Aceptación de Observaciones',
            };

            const rows = [
              { label: 'Tipo de Acción', value: tipoLabels[auditModal.tipo_accion] || auditModal.tipo_accion },
              { label: 'Fase', value: auditModal.fase },
              { label: 'IP', value: auditModal.ip || '-' },
              { label: 'X-Forwarded-For', value: auditModal.x_forwarded_for || '-' },
              { label: 'User-Agent', value: auditModal.user_agent || '-', mono: true },
              { label: 'Fecha/Hora Cliente', value: auditModal.client_timestamp ? new Date(auditModal.client_timestamp).toLocaleString('es-PE', { dateStyle: 'full', timeStyle: 'long' }) : '-' },
              { label: 'Fecha/Hora Servidor', value: auditModal.server_timestamp ? new Date(auditModal.server_timestamp).toLocaleString('es-PE', { dateStyle: 'full', timeStyle: 'long' }) : '-' },
              { label: 'Resolución Pantalla', value: auditModal.screen_resolution || '-' },
              { label: 'Plataforma', value: auditModal.platform || '-' },
              { label: 'Idioma', value: auditModal.language || '-' },
            ];

            if (meta.fecha_seleccionada) {
              rows.push({
                label: 'Opción Seleccionada',
                value: `Opción ${(meta.opcion_idx || 0) + 1} — ${meta.fecha_seleccionada.fecha} ${meta.fecha_seleccionada.hora_inicio}-${meta.fecha_seleccionada.hora_fin}`
              });
            }
            if (meta.opciones_disponibles) {
              rows.push({ label: 'Opciones Disponibles', value: meta.opciones_disponibles });
            }
            if (meta.token_used) {
              rows.push({ label: 'Token Utilizado', value: meta.token_used, mono: true });
            }
            if (auditModal.email_audit_id) {
              rows.push({
                label: 'Correo Origen',
                value: (
                  <button
                    onClick={() => {
                      setAuditModal(null);
                      const fullLog = emailLogs.find(e => e.id === auditModal.email_audit_id);
                      if (fullLog) {
                        setEmailPreview(fullLog);
                      } else {
                        setEmailPreview({ id: auditModal.email_audit_id, subject: auditModal.email_audit_subject, sender: '', sent_at: '', recipient_email: '', recipient_name: '' });
                      }
                    }}
                    className="inline-flex items-center gap-1.5 text-[11px] font-medium text-[#9d8272] bg-[#faf8f6] border border-[#d4c5bb] rounded-md px-2.5 py-1.5 hover:bg-[#f0ebe5] transition-colors"
                    data-testid="btn-correo-origen"
                  >
                    <Mail size={12} />
                    {auditModal.email_audit_subject || 'Ver correo enviado'}
                  </button>
                )
              });
            }

            return (
              <div className="max-h-[500px] overflow-y-auto">
                <table className="w-full text-sm" data-testid="tabla-audit-detalle">
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-[#fafafa]' : 'bg-white'}>
                        <td className="px-5 py-2.5 text-[11px] font-semibold text-[#888] uppercase w-[180px] align-top">{r.label}</td>
                        <td className={`px-5 py-2.5 text-xs text-[#333] ${r.mono ? 'font-mono text-[10px] break-all' : ''}`}>{r.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
          <div className="px-5 py-3 border-t border-[#e8e8e8] bg-[#fafafa]">
            <p className="text-[10px] text-[#aaa]">
              Registro anti-repudio — Datos capturados al momento de la confirmación por parte del cliente/propietario.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: Previsualización de PDF */}
      <Dialog open={!!pdfPreview} onOpenChange={(open) => { if (!open) setPdfPreview(null); }}>
        <DialogContent className="max-w-4xl h-[90vh] flex flex-col" data-testid="modal-pdf-preview">
          <DialogHeader>
            <DialogTitle className="text-sm font-semibold text-[#4E7A79] flex items-center gap-2">
              <FileText size={16} />
              {pdfPreview?.nombre || 'Documento'}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0">
            {pdfPreview?.url && (
              <iframe
                src={pdfPreview.url}
                className="w-full h-full rounded border border-[#E8E8E7]"
                title={pdfPreview.nombre || 'PDF'}
              />
            )}
          </div>
          <div className="flex justify-end pt-2">
            <Button
              variant="outline" size="sm"
              className="text-xs"
              onClick={() => window.open(pdfPreview?.url, '_blank')}
              data-testid="btn-pdf-nueva-pestana"
            >
              <ExternalLink size={12} className="mr-1" /> Abrir en nueva pestaña
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: Listado de documentos posteriores al contrato */}
      <Dialog open={showDocsList} onOpenChange={setShowDocsList}>
        <DialogContent className="max-w-lg" data-testid="modal-docs-list">
          <DialogHeader>
            <DialogTitle className="text-sm font-semibold text-[#4E7A79]">
              Adendas
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-1">
            <div className="grid grid-cols-[40px_1fr_100px_40px] gap-2 text-xs font-semibold text-[#888] border-b pb-1 px-1">
              <span>Orden</span>
              <span>Nombre</span>
              <span>Fecha de Carga</span>
              <span></span>
            </div>
            {docsEfectivos.adendas
              .filter(d => d.url)
              .sort((a, b) => (a.fecha_carga || '').localeCompare(b.fecha_carga || ''))
              .map((doc, idx) => (
                <div key={idx} className="grid grid-cols-[40px_1fr_100px_40px] gap-2 items-center text-sm px-1 py-1.5 hover:bg-[#F8F8F8] rounded">
                  <span className="text-[#888] text-xs">{idx + 1}</span>
                  <span className="truncate" title={doc.nombre}>{doc.nombre || `Documento ${idx + 1}`}</span>
                  <span className="text-xs text-[#888]">{doc.fecha_carga || '-'}</span>
                  <Button
                    variant="ghost" size="sm"
                    className="h-6 w-6 p-0 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                    data-testid={`btn-ver-doc-${idx}`}
                    title={`Ver ${doc.nombre || 'documento'}`}
                    onClick={() => {
                      setPdfPreview({ url: doc.url, nombre: doc.nombre || `Documento ${idx + 1}` });
                    }}
                  >
                    <Eye size={14} />
                  </Button>
                </div>
              ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const InfoField = ({ label, value }) => (
  <div>
    <span className="text-[11px] text-[#888] block">{label}</span>
    {typeof value === 'string' || typeof value === 'number' ? (
      <span className="text-[#2D2D2D] text-sm">{value || '-'}</span>
    ) : (
      value || <span className="text-[#2D2D2D] text-sm">-</span>
    )}
  </div>
);

const CitacionNotarialInfo = ({ entregaId, codigo }) => {
  const [citacion, setCitacion] = React.useState(null);
  const [downloadingPdf, setDownloadingPdf] = React.useState(false);

  React.useEffect(() => {
    api.get(`/entregas/citaciones/${entregaId}`).then(r => setCitacion(r.data)).catch(() => {});
  }, [entregaId]);

  const descargarPDF = async () => {
    setDownloadingPdf(true);
    try {
      const resp = await api.get(`/entregas/citaciones/${entregaId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Certificado_Inasistencia_${codigo}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Error al generar PDF'); }
    finally { setDownloadingPdf(false); }
  };

  if (!citacion) return null;
  return (
    <Card className="border border-red-200 bg-red-50/30" data-testid="info-citacion-notarial">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-red-700 flex items-center gap-1.5">
          <FileText size={14} /> Citación Notarial
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-[10px] text-[#888] block">Notaría</span><span className="text-[#2D2D2D]">{citacion.notaria_nombre}</span></div>
          <div><span className="text-[10px] text-[#888] block">Dirección</span><span className="text-[#2D2D2D]">{citacion.notaria_direccion || '-'}</span></div>
          <div><span className="text-[10px] text-[#888] block">Fecha Citación</span><span className="text-[#2D2D2D]">{citacion.fecha_citacion?.substring(0, 10) || '-'}</span></div>
          <div><span className="text-[10px] text-[#888] block">N° Acta</span><span className="text-[#2D2D2D]">{citacion.numero_acta || '-'}</span></div>
        </div>
        <Button variant="outline" size="sm" onClick={descargarPDF} disabled={downloadingPdf} className="text-xs" data-testid="btn-cert-pdf">
          {downloadingPdf ? <Loader2 size={12} className="animate-spin mr-1" /> : <Download size={12} className="mr-1" />}
          {downloadingPdf ? 'Generando...' : 'Certificado PDF'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default EntregaDetalle;
